import React, { createContext, useContext, useEffect, useState } from "react";
import { authService } from "../services/api";

// Criar o contexto
const AuthContext = createContext({});

// Hook personalizado para usar o contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth deve ser usado dentro de um AuthProvider");
  }
  return context;
};

// Provider do contexto
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Verificar token no localStorage ao inicializar
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const storedToken = localStorage.getItem("authToken");
        const storedUser = localStorage.getItem("authUser");

        if (storedToken && storedUser) {
          // Verificar se o token ainda é válido
          try {
            await authService.verify();
            setToken(storedToken);
            setUser(JSON.parse(storedUser));
          } catch (error) {
            // Token inválido, limpar dados
            localStorage.removeItem("authToken");
            localStorage.removeItem("authUser");
          }
        }
      } catch (error) {
        console.error("Erro ao verificar autenticação:", error);
        // Limpar dados corrompidos
        localStorage.removeItem("authToken");
        localStorage.removeItem("authUser");
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Função de login
  const login = async (credentials) => {
    try {
      setLoading(true);

      // Chamada real para a API
      const response = await authService.login(credentials);

      // Armazenar no estado
      setUser(response.usuario);
      setToken(response.token);

      // Armazenar no localStorage
      localStorage.setItem("authToken", response.token);
      localStorage.setItem("authUser", JSON.stringify(response.usuario));

      return { success: true, user: response.usuario };
    } catch (error) {
      console.error("Erro no login:", error);
      return {
        success: false,
        message: error.message || "Erro ao fazer login",
      };
    } finally {
      setLoading(false);
    }
  };

  // Função de logout
  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("authToken");
    localStorage.removeItem("authUser");
  };

  // Função para verificar se está autenticado
  const isAuthenticated = () => !!user;

  // Função para verificar permissões com base na role
  const hasPermission = (requiredRole) => {
    if (!user || !user.role) return false;
    // Por enquanto, consideramos 'admin' como tendo mais permissões que 'padrao'
    if (requiredRole === "admin") {
      return user.role === "admin";
    }
    return true; // Todos os usuários logados podem ler/escrever dados do seu escritório
  };

  // Função para atualizar dados do usuário
  const updateUser = (userData) => {
    const updatedUser = { ...user, ...userData };
    setUser(updatedUser);
    localStorage.setItem("authUser", JSON.stringify(updatedUser));
  };

  // Valor do contexto
  const contextValue = {
    // Estado
    user,
    token,
    loading,

    // Funções de autenticação
    login,
    logout,
    isAuthenticated,
    hasPermission,
    updateUser,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export default AuthContext;
